import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Button,
  CircularProgress,
  Paper,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { BRAND } from '../../config/brand';
import { useGetWorkOrdersQuery } from '../../api/workOrderApi';
import { useAdvanceStageMutation } from '../../api/productionFlowApi';
import EntityDataTable, { EntityColumn } from '../common/EntityDataTable';

interface WorkOrderRow {
  id: string;
  workOrderNumber: string;
  productType: string;
  status: string;
  currentStage: string;
  priority: number;
  order?: { customer?: { name?: string } };
  productionLine?: { name?: string };
}

export const ProductionQueue: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.startsWith('/worker')
    ? '/worker'
    : location.pathname.startsWith('/manager')
      ? '/manager'
      : '/admin';

  const { data: workOrders = [], isLoading, refetch } = useGetWorkOrdersQuery();
  const [advanceStage] = useAdvanceStageMutation();

  const columns: EntityColumn<WorkOrderRow>[] = [
    { id: 'workOrderNumber', label: 'WO #' },
    {
      id: 'customer',
      label: 'Customer',
      render: (row) => row.order?.customer?.name || '—',
    },
    { id: 'productType', label: 'Product Type' },
    { id: 'currentStage', label: 'Stage' },
    { id: 'priority', label: 'Priority', render: (row) => `P${row.priority}` },
    {
      id: 'line',
      label: 'Line',
      render: (row) => row.productionLine?.name || '—',
    },
    {
      id: 'advance',
      label: 'Quick Action',
      render: (row) => (
        <Button
          size="small"
          variant="outlined"
          onClick={async () => {
            await advanceStage(row.id);
            refetch();
          }}
          sx={{ borderColor: BRAND.colors.navy, color: BRAND.colors.navy }}
        >
          Advance
        </Button>
      ),
    },
  ];

  const queued = (workOrders as WorkOrderRow[]).filter(
    (wo) => wo.status === 'queued' || wo.status === 'design' || wo.currentStage === 'queue',
  );

  return (
    <Box>
      <Typography variant="h4" sx={{ color: BRAND.colors.navy, fontWeight: 700, mb: 1 }}>
        Production Queue
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Work orders awaiting or in active production — wired to work-orders API
      </Typography>

      <Paper sx={{ p: 2, mb: 3, display: 'flex', gap: 2 }}>
        <Chip label={`Total: ${workOrders.length}`} />
        <Chip label={`Queued: ${queued.length}`} color="warning" />
        <Chip
          label={`In Progress: ${(workOrders as WorkOrderRow[]).filter((w) => w.status === 'in_progress').length}`}
          color="primary"
        />
      </Paper>

      {isLoading ? (
        <CircularProgress sx={{ color: BRAND.colors.navy }} />
      ) : (
        <EntityDataTable
          data={workOrders as WorkOrderRow[]}
          columns={columns}
          statusField="status"
          statusColors={{
            design: 'info',
            queued: 'warning',
            in_progress: 'primary',
            qc: 'secondary',
            completed: 'success',
            cancelled: 'error',
          }}
          viewPath={(row) => `${basePath}/production/work-orders/${row.id}`}
          searchFields={['workOrderNumber', 'productType', 'currentStage']}
        />
      )}
    </Box>
  );
};

export default ProductionQueue;
